import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Boxes, Plus, Trash2, Check, X, MapPin, Clock, HelpCircle, ArrowLeft } from "lucide-react";
import { ImageUpload } from "@/components/ImageUpload";
import { toast } from "sonner";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  active: "bg-green-100 text-green-800",
  rejected: "bg-neutral-200 text-neutral-600",
};

function discounted(s) {
  const base = Number(s.price) || 0;
  const dv = Number(s.package_discount_value) || 0;
  if (s.package_discount_type === "percent") return +(base - (base * Math.min(dv, 100)) / 100).toFixed(2);
  if (s.package_discount_type === "flat") return +(base - Math.min(dv, base)).toFixed(2);
  return base;
}

export default function ProviderPackages() {
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [myServices, setMyServices] = useState([]);
  const [discover, setDiscover] = useState({ city: null, services: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", my_service_id: "", partner_service_ids: [], cover_url: "" });

  const canCreate = user && (user.tier === "intermediate" || user.tier === "pro");

  const load = () => {
    api.get("/packages/mine").then((r) => setPackages(r.data));
    api.get("/services/mine").then((r) => setMyServices(r.data.filter((s) => s.active)));
    api.get("/packages/discover").then((r) => setDiscover(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const togglePartner = (id) =>
    setForm((f) => ({
      ...f,
      partner_service_ids: f.partner_service_ids.includes(id)
        ? f.partner_service_ids.filter((x) => x !== id)
        : [...f.partner_service_ids, id],
    }));

  const selectedPartners = discover.services.filter((s) => form.partner_service_ids.includes(s.id));
  const mySvc = myServices.find((s) => s.id === form.my_service_id);
  const previewItems = [...(mySvc ? [mySvc] : []), ...selectedPartners];
  const previewTotal = previewItems.reduce((a, s) => a + discounted(s), 0);
  const previewBase = previewItems.reduce((a, s) => a + (Number(s.price) || 0), 0);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/packages", form);
      toast.success("Package proposal sent to partner(s)");
      setOpen(false);
      setForm({ name: "", description: "", my_service_id: "", partner_service_ids: [], cover_url: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const respond = async (id, action) => {
    try {
      const { data } = await api.post(`/packages/${id}/respond`, { action });
      toast.success(action === "accept" ? (data.all_accepted ? "Package is now live!" : "Accepted") : "Declined");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/packages/${id}`); toast.success("Package removed"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="provider-packages-page">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-to-dashboard">
        <ArrowLeft className="w-4 h-4" />Back to dashboard
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-primary"><Boxes className="w-5 h-5" /><span className="text-xs uppercase tracking-[0.2em] font-bold">Collaborative bundles</span></div>
          <h1 className="font-display text-4xl font-semibold tracking-tighter mt-1">Packages</h1>
          <p className="text-muted-foreground mt-1">Pair your service with another provider's and offer a discounted bundle.</p>
          <Link to="/provider/packages/guide" className="inline-flex items-center gap-1 text-sm text-primary font-medium mt-2 hover:underline" data-testid="packages-guide-link">
            <HelpCircle className="w-4 h-4" /> How packages work
          </Link>
        </div>
        {canCreate ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm" data-testid="create-package-btn"><Plus className="w-4 h-4 mr-1" />Create package</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-2xl">Propose a package</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div><Label>Package name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required className="mt-1 rounded-sm" data-testid="package-name" /></div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="mt-1 rounded-sm" data-testid="package-desc" /></div>
                <ImageUpload value={form.cover_url} onChange={(u) => setForm((f) => ({ ...f, cover_url: u }))} label="Cover image (optional)" testid="package-cover" />
                <div>
                  <Label>Your service</Label>
                  <Select value={form.my_service_id} onValueChange={(v) => setForm((f) => ({ ...f, my_service_id: v }))}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="package-my-service"><SelectValue placeholder="Select your service" /></SelectTrigger>
                    <SelectContent>{myServices.map((s) => <SelectItem key={s.id} value={s.id}>{s.title} — ${s.price}</SelectItem>)}</SelectContent>
                  </Select>
                  {myServices.length === 0 && <p className="text-xs text-muted-foreground mt-1">Create an active service first.</p>}
                </div>
                <div>
                  <Label>Partner services {discover.city ? <span className="text-muted-foreground font-normal">in {discover.city}</span> : null}</Label>
                  {discover.services.length === 0 ? (
                    <p className="text-sm text-muted-foreground mt-2" data-testid="no-partner-services">
                      No other providers' services found{discover.city ? ` in ${discover.city}` : ""}. Set your City in Dashboard → Business profile to match local partners.
                    </p>
                  ) : (
                    <div className="mt-2 space-y-2 max-h-64 overflow-y-auto border border-border rounded-sm p-2">
                      {discover.services.map((s) => (
                        <label key={s.id} data-testid={`partner-svc-${s.id}`}
                          className={`flex items-center gap-3 p-2 rounded-sm cursor-pointer border ${form.partner_service_ids.includes(s.id) ? "border-primary bg-primary/5" : "border-transparent hover:bg-muted"}`}>
                          <input type="checkbox" checked={form.partner_service_ids.includes(s.id)} onChange={() => togglePartner(s.id)} className="accent-current" />
                          <div className="flex-1">
                            <div className="text-sm font-medium">{s.title} <span className="text-xs text-muted-foreground">· {s.subscriber_name}</span></div>
                            <div className="text-xs text-muted-foreground">{s.category} · ${s.price}{s.package_discount_type ? ` → $${discounted(s)} in bundle` : " · no bundle discount set"}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {previewItems.length > 0 && (
                  <div className="rounded-sm border border-border bg-muted/40 p-4 text-sm" data-testid="package-preview">
                    <div className="flex justify-between"><span className="text-muted-foreground">Regular total</span><span className="line-through">${previewBase.toFixed(2)}</span></div>
                    <div className="flex justify-between font-semibold text-primary mt-1"><span>Package price</span><span data-testid="package-preview-total">${previewTotal.toFixed(2)}</span></div>
                  </div>
                )}
                <DialogFooter>
                  <Button type="submit" className="rounded-sm w-full" disabled={!form.name || !form.my_service_id || form.partner_service_ids.length === 0} data-testid="submit-package-btn">Send proposal</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        ) : (
          <div className="text-sm text-muted-foreground border border-border rounded-sm px-4 py-3 max-w-xs" data-testid="basic-package-note">
            Your Basic plan is <b>invite-only</b> for packages. <Link to="/pricing" className="text-primary font-medium">Upgrade</Link> to create your own.
          </div>
        )}
      </div>

      {packages.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-12 text-center mt-10" data-testid="packages-empty">
          <p className="text-muted-foreground">No packages yet. {canCreate ? "Propose one to a nearby provider." : "You'll see invitations here."}</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4 mt-10">
          {packages.map((p) => (
            <div key={p.id} className="bg-white border border-border rounded-sm p-5" data-testid={`package-card-${p.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-display text-xl font-medium">{p.name}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{p.is_creator ? "Created by you" : `Proposed by ${p.creator_name}`}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-sm font-medium ${STATUS_STYLE[p.status]}`} data-testid={`package-status-${p.id}`}>{p.status}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">{p.description || "—"}</p>
              <div className="mt-3 space-y-1.5 border-t border-border pt-3">
                {p.items.map((i) => (
                  <div key={i.service_id} className="flex justify-between text-sm">
                    <span>{i.service_title} <span className="text-xs text-muted-foreground">· {i.provider_name}</span> {i.accepted ? <Check className="inline w-3.5 h-3.5 text-green-600" /> : <span className="text-xs text-amber-600">(pending)</span>}</span>
                    <span className="text-muted-foreground">${i.discounted_price}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center mt-3 pt-3 border-t border-border">
                <span className="text-sm text-muted-foreground">Bundle price</span>
                <span className="font-display text-lg font-semibold text-primary">${p.total_price} <span className="text-xs text-muted-foreground line-through">${p.total_base}</span></span>
              </div>
              <div className="flex gap-2 mt-4">
                {p.needs_my_response && (
                  <>
                    <Button size="sm" className="rounded-sm flex-1" onClick={() => respond(p.id, "accept")} data-testid={`accept-package-${p.id}`}><Check className="w-4 h-4 mr-1" />Accept</Button>
                    <Button size="sm" variant="outline" className="rounded-sm flex-1" onClick={() => respond(p.id, "reject")} data-testid={`reject-package-${p.id}`}><X className="w-4 h-4 mr-1" />Decline</Button>
                  </>
                )}
                {p.is_creator && (
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={() => remove(p.id)} data-testid={`delete-package-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                )}
                {p.status === "active" && (
                  <Link to={`/packages/${p.id}`} className="flex-1"><Button size="sm" variant="outline" className="rounded-sm w-full">View public page</Button></Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
